import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  NotificationsActive as PushIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as RefreshIcon,
  WhatsApp as WhatsAppIcon,
  Psychology as AiIcon,
  ExpandMore as ExpandMoreIcon,
  QrCode2 as QrCodeIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';
import KooppaiIcon from './KooppaiIcon';
import axios from 'axios';
import Swal from 'sweetalert2';

const NotificationSettings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    emailPassword: false,
    twilioAuthToken: false,
    nexmoSecret: false,
    plivoToken: false,
    awsSecret: false,
    vapidPrivateKey: false,
    kooppaiApiKey: false,
    whatsappApiKey: false,
    aiApiKey: false
  });
  const [firebaseFileName, setFirebaseFileName] = useState('');
  
  // WhatsApp Web QR Code states
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  const [config, setConfig] = useState({
    email: {
      enabled: false,
      provider: 'SMTP',
      host: '',
      port: 587,
      secure: false,
      user: '',
      password: '',
      from: '',
      fromName: ''
    },
    sms: {
      enabled: false,
      provider: 'TWILIO',
      accountSid: '',
      authToken: '',
      fromNumber: '',
      apiKey: '',
      senderId: '',
      accessKeyId: '',
      secretAccessKey: '',
      region: ''
    },
    push: {
      enabled: false,
      provider: 'WEB_PUSH',
      subject: '',
      publicKey: '',
      privateKey: '',
      serviceAccountKey: null,
      fcmServerKey: ''
    },
    defaults: {
      channels: {
        inApp: true,
        email: true,
        sms: false,
        push: true
      },
      emailFrequency: 'INSTANT'
    },
    rateLimits: {
      email: {
        perHour: 100,
        perDay: 1000
      },
      sms: {
        perHour: 50,
        perDay: 200
      }
    },
    templates: {
      email: {
        header: '',
        footer: '',
        primaryColor: '#4CAF50',
        logo: ''
      },
      sms: {
        prefix: '',
        suffix: ''
      }
    },
    // Kooppai Configuration (Primary Messaging Channel)
    kooppai: {
      enabled: true,
      apiUrl: 'http://localhost:5000/api',
      apiKey: '',
      defaultForCampaigns: true
    },
    // WhatsApp Configuration
    whatsapp: {
      enabled: false,
      provider: 'WHATSAPP_WEB',
      // WhatsApp Web (QR Code)
      qrLinked: false,
      qrLinkedAt: null,
      qrSessionId: '',
      // Billing Notifications
      billingNotifications: {
        dineIn: { orderCreated: false, ready: false, payment: false },
        delivery: { orderCreated: false, delivery: false, payment: false },
        takeAway: { orderCreated: false, payment: false },
        customerCredit: ''
      },
      // Shift Report Phones
      shiftReportPhones: {
        billingAdvanceShift: [{ countryCode: '+91', phoneNumber: '' }],
        salesShift: [{ countryCode: '+91', phoneNumber: '' }]
      },
      // API Providers
      accountSid: '',
      authToken: '',
      fromNumber: '',
      businessApiUrl: '',
      accessToken: '',
      phoneNumberId: '',
      businessAccountId: '',
      watiApiUrl: '',
      watiApiKey: '',
      interaktApiKey: '',
      interaktAppId: '',
      gupshupApiKey: '',
      gupshupAppName: '',
      messagebirdApiKey: '',
      messagebirdChannelId: ''
    },
    // AI Configuration (Gemini)
    ai: {
      enabled: false,
      provider: 'GEMINI'
    },
    aiApiKey: ''
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/notification-config');
      if (response.data.success) {
        // Merge fetched config with default state to preserve all fields
        const fetchedConfig = response.data.data;
        setConfig(prev => ({
          email: {
            ...prev.email,
            ...(fetchedConfig.email || {}),
            // Ensure empty strings instead of undefined
            host: fetchedConfig.email?.host || '',
            user: fetchedConfig.email?.user || '',
            password: fetchedConfig.email?.password || '',
            from: fetchedConfig.email?.from || '',
            fromName: fetchedConfig.email?.fromName || ''
          },
          sms: {
            ...prev.sms,
            ...(fetchedConfig.sms || {})
          },
          push: {
            ...prev.push,
            ...(fetchedConfig.push || {})
          },
          defaults: {
            ...prev.defaults,
            ...(fetchedConfig.defaults || {})
          },
          rateLimits: {
            ...prev.rateLimits,
            ...(fetchedConfig.rateLimits || {})
          },
          templates: {
            ...prev.templates,
            ...(fetchedConfig.templates || {})
          },
          kooppai: {
            ...prev.kooppai,
            ...(fetchedConfig.kooppai || {})
          },
          whatsapp: {
            ...prev.whatsapp,
            ...(fetchedConfig.whatsapp || {}),
            billingNotifications: {
              ...prev.whatsapp.billingNotifications,
              ...(fetchedConfig.whatsapp?.billingNotifications || {})
            },
            shiftReportPhones: {
              billingAdvanceShift: fetchedConfig.whatsapp?.shiftReportPhones?.billingAdvanceShift?.length > 0 
                ? fetchedConfig.whatsapp.shiftReportPhones.billingAdvanceShift 
                : prev.whatsapp.shiftReportPhones.billingAdvanceShift,
              salesShift: fetchedConfig.whatsapp?.shiftReportPhones?.salesShift?.length > 0 
                ? fetchedConfig.whatsapp.shiftReportPhones.salesShift 
                : prev.whatsapp.shiftReportPhones.salesShift
            }
          },
          ai: {
            ...prev.ai,
            ...(fetchedConfig.ai || {})
          },
          aiApiKey: fetchedConfig.aiApiKey || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        ...config,
        aiApiKey: config.ai?.enabled ? (config.aiApiKey || '') : ''
      };
      console.log('💾 Saving notification config:', payload);
      const response = await axios.post('/api/notification-config/update', payload);
      if (response.data.success) {
        console.log('✅ Config saved successfully:', response.data.data);
        Swal.fire('Success', 'Configuration saved successfully', 'success');
      }
    } catch (error) {
      console.error('❌ Failed to save config:', error);
      Swal.fire('Error', 'Failed to save configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async (service) => {
    try {
      const response = await axios.post(`/api/notification-config/test-${service}`);
      if (response.data.success) {
        Swal.fire('Success', `${service.toUpperCase()} connection successful!`, 'success');
      }
    } catch (error) {
      Swal.fire('Error', `${service.toUpperCase()} connection failed`, 'error');
    }
  };

  const generateVapidKeys = async () => {
    try {
      const response = await axios.post('/api/notification-config/generate-vapid');
      if (response.data.success) {
        setConfig(prev => ({
          ...prev,
          push: {
            ...prev.push,
            publicKey: response.data.publicKey,
            privateKey: response.data.privateKey
          }
        }));
        Swal.fire('Success', 'VAPID keys generated successfully', 'success');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to generate VAPID keys', 'error');
    }
  };

  const handleFirebaseServiceAccountUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        if (typeof text !== 'string') {
          throw new Error('Invalid file contents');
        }
        const json = JSON.parse(text);

        setConfig((prev) => ({
          ...prev,
          push: {
            ...prev.push,
            provider: 'FIREBASE',
            serviceAccountKey: json
          }
        }));
        setFirebaseFileName(file.name);
        Swal.fire('Success', 'Firebase service account loaded. Click Save Changes to apply.', 'success');
      } catch (err) {
        console.error('Error parsing Firebase service account JSON:', err);
        Swal.fire('Error', 'Invalid Firebase service account JSON file', 'error');
      }
    };

    reader.readAsText(file);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const updateConfig = (section, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateNestedConfig = (section, subsection, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  // WhatsApp Web QR Code functions
  const handleLinkWhatsAppDevice = async () => {
    setQrLoading(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('login_meta') || '{}');
      const userId = userInfo?.data?._id || userInfo?._id;
      const dbName = userInfo?.data?.databaseName || userInfo?.databaseName || localStorage.getItem('DataBaseName') || '';
      
      console.log('Linking WhatsApp device:', { userId, dbName });
      
      // Start linking device
      await axios.post('/sendToWs/link-device', { userId, db: dbName });
      
      // Wait and retry to get QR code (it takes a few seconds to generate)
      let qrCodeValue = null;
      let attempts = 0;
      const maxAttempts = 15;
      
      while (!qrCodeValue && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds
        attempts++;
        
        try {
          const qrRes = await axios.get(`/sendToWs/get-qr/${userId}?db=${dbName}`);
          console.log(`Attempt ${attempts}: QR response:`, qrRes.data);
          // Backend returns qrCode, not qr
          if (qrRes.data?.qrCode) {
            qrCodeValue = qrRes.data.qrCode;
          } else if (qrRes.data?.status === 'ready') {
            Swal.fire('Success', 'Device already linked!', 'success');
            setConfig(prev => ({
              ...prev,
              whatsapp: { ...prev.whatsapp, qrLinked: true, qrLinkedAt: new Date().toISOString() }
            }));
            return;
          }
        } catch (e) {
          console.log(`Attempt ${attempts}: QR not ready yet...`, e.response?.data);
        }
      }
      
      if (qrCodeValue) {
        setQrCode(qrCodeValue);
        setQrDialogOpen(true);
      } else {
        Swal.fire('Info', 'QR code generation timed out. Please try again.', 'info');
      }
    } catch (error) {
      console.error('Error linking device:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to generate QR code', 'error');
    } finally {
      setQrLoading(false);
    }
  };

  const handleWhatsAppLogout = async () => {
    const result = await Swal.fire({
      title: 'Logout WhatsApp?',
      text: 'This will disconnect your WhatsApp session.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Logout'
    });
    
    if (result.isConfirmed) {
      try {
        const userInfo = JSON.parse(localStorage.getItem('login_meta') || '{}');
        const userId = userInfo?.data?._id || userInfo?._id;
        
        await axios.post('/sendToWs/logout', { userId });
        setConfig(prev => ({
          ...prev,
          whatsapp: { ...prev.whatsapp, qrLinked: false, qrLinkedAt: null }
        }));
        Swal.fire('Success', 'WhatsApp disconnected successfully', 'success');
      } catch (error) {
        Swal.fire('Error', 'Failed to logout', 'error');
      }
    }
  };

  const handleQrDialogClose = () => {
    setQrDialogOpen(false);
    setQrCode(null);
    // Mark as linked after QR scan
    setConfig(prev => ({
      ...prev,
      whatsapp: { ...prev.whatsapp, qrLinked: true, qrLinkedAt: new Date().toISOString() }
    }));
  };

  // Update billing notification checkbox
  const updateBillingNotification = (category, field, value) => {
    setConfig(prev => ({
      ...prev,
      whatsapp: {
        ...prev.whatsapp,
        billingNotifications: {
          ...prev.whatsapp.billingNotifications,
          [category]: {
            ...prev.whatsapp.billingNotifications[category],
            [field]: value
          }
        }
      }
    }));
  };

  // Add/Remove shift report phone
  const addShiftPhone = (shiftType) => {
    setConfig(prev => ({
      ...prev,
      whatsapp: {
        ...prev.whatsapp,
        shiftReportPhones: {
          ...prev.whatsapp.shiftReportPhones,
          [shiftType]: [...(prev.whatsapp.shiftReportPhones[shiftType] || []), { countryCode: '+91', phoneNumber: '' }]
        }
      }
    }));
  };

  const removeShiftPhone = (shiftType, index) => {
    setConfig(prev => ({
      ...prev,
      whatsapp: {
        ...prev.whatsapp,
        shiftReportPhones: {
          ...prev.whatsapp.shiftReportPhones,
          [shiftType]: prev.whatsapp.shiftReportPhones[shiftType].filter((_, i) => i !== index)
        }
      }
    }));
  };

  const updateShiftPhone = (shiftType, index, field, value) => {
    setConfig(prev => ({
      ...prev,
      whatsapp: {
        ...prev.whatsapp,
        shiftReportPhones: {
          ...prev.whatsapp.shiftReportPhones,
          [shiftType]: prev.whatsapp.shiftReportPhones[shiftType].map((phone, i) => 
            i === index ? { ...phone, [field]: value } : phone
          )
        }
      }
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Notification Settings
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchConfig}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Save Changes
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<KooppaiIcon sx={{ color: '#1976d2' }} />} label="Kooppai" />
          <Tab icon={<WhatsAppIcon sx={{ color: '#25D366' }} />} label="WhatsApp" />
          <Tab icon={<AiIcon sx={{ color: '#9c27b0' }} />} label="AI" />
          <Tab icon={<EmailIcon />} label="Email" />
          <Tab icon={<SmsIcon />} label="SMS" />
          <Tab icon={<PushIcon />} label="Push" />
          <Tab icon={<SettingsIcon />} label="General" />
        </Tabs>
      </Paper>

      {/* Kooppai Configuration - Tab 0 */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            💬 Kooppai Configuration (Primary Messaging Channel)
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Kooppai is your primary messaging platform for campaigns and notifications. 
            It provides WhatsApp-like messaging capabilities with your own infrastructure.
          </Alert>

          <FormControlLabel
            control={
              <Switch
                checked={config.kooppai?.enabled ?? true}
                onChange={(e) => updateConfig('kooppai', 'enabled', e.target.checked)}
              />
            }
            label="Enable Kooppai Messaging"
            sx={{ mb: 3 }}
          />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Kooppai API URL"
                value={config.kooppai?.apiUrl || ''}
                onChange={(e) => updateConfig('kooppai', 'apiUrl', e.target.value)}
                placeholder="http://localhost:5000/api"
                helperText="URL of your Kooppai backend server"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type={showPasswords.kooppaiApiKey ? 'text' : 'password'}
                label="Kooppai API Key"
                value={config.kooppai?.apiKey || ''}
                onChange={(e) => updateConfig('kooppai', 'apiKey', e.target.value)}
                placeholder="Enter your Kooppai API key"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => togglePasswordVisibility('kooppaiApiKey')}>
                        {showPasswords.kooppaiApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.kooppai?.defaultForCampaigns ?? true}
                    onChange={(e) => updateConfig('kooppai', 'defaultForCampaigns', e.target.checked)}
                  />
                }
                label="Use Kooppai as default channel for campaigns"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={() => testConnection('kooppai')}
                disabled={!config.kooppai?.enabled}
              >
                Test Kooppai Connection
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* WhatsApp Configuration - Tab 1 */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <WhatsAppIcon sx={{ color: '#25D366', mr: 1, verticalAlign: 'middle' }} />
            WhatsApp Configuration
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={config.whatsapp?.enabled ?? false}
                onChange={(e) => updateConfig('whatsapp', 'enabled', e.target.checked)}
              />
            }
            label="Enable WhatsApp Messaging"
            sx={{ mb: 3 }}
          />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>WhatsApp Provider</InputLabel>
                <Select
                  value={config.whatsapp?.provider || 'WHATSAPP_WEB'}
                  onChange={(e) => updateConfig('whatsapp', 'provider', e.target.value)}
                  label="WhatsApp Provider"
                >
                  <MenuItem value="WHATSAPP_WEB">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <QrCodeIcon fontSize="small" />
                      WhatsApp Web (QR Code) - Free
                    </Box>
                  </MenuItem>
                  <MenuItem value="TWILIO">Twilio WhatsApp</MenuItem>
                  <MenuItem value="META">Meta Business API (Official)</MenuItem>
                  <MenuItem value="WATI">WATI</MenuItem>
                  <MenuItem value="INTERAKT">Interakt</MenuItem>
                  <MenuItem value="GUPSHUP">Gupshup</MenuItem>
                  <MenuItem value="MESSAGEBIRD">MessageBird</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* WhatsApp Web (QR Code) - Free Service */}
            {config.whatsapp?.provider === 'WHATSAPP_WEB' && (
              <Grid item xs={12}>
                <Card sx={{ bgcolor: '#f5f5f5', mt: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QrCodeIcon sx={{ color: '#25D366' }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          WhatsApp Web Connection
                        </Typography>
                        {config.whatsapp?.qrLinked && (
                          <Chip 
                            icon={<CheckCircleIcon />} 
                            label="Connected" 
                            color="success" 
                            size="small" 
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={qrLoading ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                          onClick={handleLinkWhatsAppDevice}
                          disabled={qrLoading}
                        >
                          {qrLoading ? 'Generating...' : 'Link Device'}
                        </Button>
                        {config.whatsapp?.qrLinked && (
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<LinkOffIcon />}
                            onClick={handleWhatsAppLogout}
                          >
                            Logout
                          </Button>
                        )}
                      </Box>
                    </Box>
                    
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <strong>Note:</strong> This is a free QR service and may be discontinued at any time without prior notice.
                      For production use, consider using official WhatsApp Business API providers.
                    </Alert>

                    {/* Billing Notifications Section */}
                    {config.whatsapp?.qrLinked && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                          📋 Billing Notifications
                        </Typography>
                        
                        <Accordion defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={500}>Dine In</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                              <FormControlLabel
                                control={<Checkbox checked={config.whatsapp?.billingNotifications?.dineIn?.orderCreated || false} 
                                  onChange={(e) => updateBillingNotification('dineIn', 'orderCreated', e.target.checked)} />}
                                label="Order Created"
                              />
                              <FormControlLabel
                                control={<Checkbox checked={config.whatsapp?.billingNotifications?.dineIn?.ready || false}
                                  onChange={(e) => updateBillingNotification('dineIn', 'ready', e.target.checked)} />}
                                label="Ready"
                              />
                              <FormControlLabel
                                control={<Checkbox checked={config.whatsapp?.billingNotifications?.dineIn?.payment || false}
                                  onChange={(e) => updateBillingNotification('dineIn', 'payment', e.target.checked)} />}
                                label="Payment"
                              />
                            </Box>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={500}>Delivery</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                              <FormControlLabel
                                control={<Checkbox checked={config.whatsapp?.billingNotifications?.delivery?.orderCreated || false}
                                  onChange={(e) => updateBillingNotification('delivery', 'orderCreated', e.target.checked)} />}
                                label="Order Created"
                              />
                              <FormControlLabel
                                control={<Checkbox checked={config.whatsapp?.billingNotifications?.delivery?.delivery || false}
                                  onChange={(e) => updateBillingNotification('delivery', 'delivery', e.target.checked)} />}
                                label="Delivery"
                              />
                              <FormControlLabel
                                control={<Checkbox checked={config.whatsapp?.billingNotifications?.delivery?.payment || false}
                                  onChange={(e) => updateBillingNotification('delivery', 'payment', e.target.checked)} />}
                                label="Payment"
                              />
                            </Box>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={500}>Take Away</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                              <FormControlLabel
                                control={<Checkbox checked={config.whatsapp?.billingNotifications?.takeAway?.orderCreated || false}
                                  onChange={(e) => updateBillingNotification('takeAway', 'orderCreated', e.target.checked)} />}
                                label="Order Created"
                              />
                              <FormControlLabel
                                control={<Checkbox checked={config.whatsapp?.billingNotifications?.takeAway?.payment || false}
                                  onChange={(e) => updateBillingNotification('takeAway', 'payment', e.target.checked)} />}
                                label="Payment"
                              />
                            </Box>
                          </AccordionDetails>
                        </Accordion>

                        {/* Shift Report Section */}
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                          📊 Shift Report Notifications
                        </Typography>

                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={500}>Billing (Advance Shift)</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {(config.whatsapp?.shiftReportPhones?.billingAdvanceShift || []).map((phone, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <TextField
                                  size="small"
                                  label="Code"
                                  value={phone.countryCode || '+91'}
                                  onChange={(e) => updateShiftPhone('billingAdvanceShift', idx, 'countryCode', e.target.value)}
                                  sx={{ width: 80 }}
                                />
                                <TextField
                                  size="small"
                                  label="Phone Number"
                                  value={phone.phoneNumber || ''}
                                  onChange={(e) => updateShiftPhone('billingAdvanceShift', idx, 'phoneNumber', e.target.value)}
                                  placeholder="9876543210"
                                  sx={{ flex: 1 }}
                                />
                                <IconButton color="error" onClick={() => removeShiftPhone('billingAdvanceShift', idx)}
                                  disabled={(config.whatsapp?.shiftReportPhones?.billingAdvanceShift || []).length <= 1}>
                                  <RemoveIcon />
                                </IconButton>
                              </Box>
                            ))}
                            <Button size="small" startIcon={<AddIcon />} onClick={() => addShiftPhone('billingAdvanceShift')}>
                              Add Phone
                            </Button>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={500}>Sales (Shift)</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {(config.whatsapp?.shiftReportPhones?.salesShift || []).map((phone, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <TextField
                                  size="small"
                                  label="Code"
                                  value={phone.countryCode || '+91'}
                                  onChange={(e) => updateShiftPhone('salesShift', idx, 'countryCode', e.target.value)}
                                  sx={{ width: 80 }}
                                />
                                <TextField
                                  size="small"
                                  label="Phone Number"
                                  value={phone.phoneNumber || ''}
                                  onChange={(e) => updateShiftPhone('salesShift', idx, 'phoneNumber', e.target.value)}
                                  placeholder="9876543210"
                                  sx={{ flex: 1 }}
                                />
                                <IconButton color="error" onClick={() => removeShiftPhone('salesShift', idx)}
                                  disabled={(config.whatsapp?.shiftReportPhones?.salesShift || []).length <= 1}>
                                  <RemoveIcon />
                                </IconButton>
                              </Box>
                            ))}
                            <Button size="small" startIcon={<AddIcon />} onClick={() => addShiftPhone('salesShift')}>
                              Add Phone
                            </Button>
                          </AccordionDetails>
                        </Accordion>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Twilio WhatsApp */}
            {config.whatsapp?.provider === 'TWILIO' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Twilio Account SID"
                    value={config.whatsapp?.accountSid || ''}
                    onChange={(e) => updateConfig('whatsapp', 'accountSid', e.target.value)}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type={showPasswords.whatsappApiKey ? 'text' : 'password'}
                    label="Twilio Auth Token"
                    value={config.whatsapp?.authToken || ''}
                    onChange={(e) => updateConfig('whatsapp', 'authToken', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => togglePasswordVisibility('whatsappApiKey')}>
                            {showPasswords.whatsappApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="WhatsApp From Number"
                    value={config.whatsapp?.fromNumber || ''}
                    onChange={(e) => updateConfig('whatsapp', 'fromNumber', e.target.value)}
                    placeholder="whatsapp:+14155238886"
                    helperText="Format: whatsapp:+1234567890"
                  />
                </Grid>
              </>
            )}

            {/* Meta Business API */}
            {config.whatsapp?.provider === 'META' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Business API URL"
                    value={config.whatsapp?.businessApiUrl || 'https://graph.facebook.com/v17.0'}
                    onChange={(e) => updateConfig('whatsapp', 'businessApiUrl', e.target.value)}
                    placeholder="https://graph.facebook.com/v17.0"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type={showPasswords.whatsappApiKey ? 'text' : 'password'}
                    label="Access Token"
                    value={config.whatsapp?.accessToken || ''}
                    onChange={(e) => updateConfig('whatsapp', 'accessToken', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => togglePasswordVisibility('whatsappApiKey')}>
                            {showPasswords.whatsappApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number ID"
                    value={config.whatsapp?.phoneNumberId || ''}
                    onChange={(e) => updateConfig('whatsapp', 'phoneNumberId', e.target.value)}
                    placeholder="Your Phone Number ID"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Business Account ID"
                    value={config.whatsapp?.businessAccountId || ''}
                    onChange={(e) => updateConfig('whatsapp', 'businessAccountId', e.target.value)}
                    placeholder="Your Business Account ID"
                  />
                </Grid>
              </>
            )}

            {/* WATI */}
            {config.whatsapp?.provider === 'WATI' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="WATI API URL"
                    value={config.whatsapp?.watiApiUrl || ''}
                    onChange={(e) => updateConfig('whatsapp', 'watiApiUrl', e.target.value)}
                    placeholder="https://live-server-xxxxx.wati.io"
                    helperText="Your WATI server URL"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type={showPasswords.whatsappApiKey ? 'text' : 'password'}
                    label="WATI API Key"
                    value={config.whatsapp?.watiApiKey || ''}
                    onChange={(e) => updateConfig('whatsapp', 'watiApiKey', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => togglePasswordVisibility('whatsappApiKey')}>
                            {showPasswords.whatsappApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </>
            )}

            {/* Interakt */}
            {config.whatsapp?.provider === 'INTERAKT' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type={showPasswords.whatsappApiKey ? 'text' : 'password'}
                    label="Interakt API Key"
                    value={config.whatsapp?.interaktApiKey || ''}
                    onChange={(e) => updateConfig('whatsapp', 'interaktApiKey', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => togglePasswordVisibility('whatsappApiKey')}>
                            {showPasswords.whatsappApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Interakt App ID"
                    value={config.whatsapp?.interaktAppId || ''}
                    onChange={(e) => updateConfig('whatsapp', 'interaktAppId', e.target.value)}
                    placeholder="Your Interakt App ID"
                  />
                </Grid>
              </>
            )}

            {/* Gupshup */}
            {config.whatsapp?.provider === 'GUPSHUP' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type={showPasswords.whatsappApiKey ? 'text' : 'password'}
                    label="Gupshup API Key"
                    value={config.whatsapp?.gupshupApiKey || ''}
                    onChange={(e) => updateConfig('whatsapp', 'gupshupApiKey', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => togglePasswordVisibility('whatsappApiKey')}>
                            {showPasswords.whatsappApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Gupshup App Name"
                    value={config.whatsapp?.gupshupAppName || ''}
                    onChange={(e) => updateConfig('whatsapp', 'gupshupAppName', e.target.value)}
                    placeholder="Your Gupshup App Name"
                  />
                </Grid>
              </>
            )}

            {/* MessageBird */}
            {config.whatsapp?.provider === 'MESSAGEBIRD' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type={showPasswords.whatsappApiKey ? 'text' : 'password'}
                    label="MessageBird API Key"
                    value={config.whatsapp?.messagebirdApiKey || ''}
                    onChange={(e) => updateConfig('whatsapp', 'messagebirdApiKey', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => togglePasswordVisibility('whatsappApiKey')}>
                            {showPasswords.whatsappApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="MessageBird Channel ID"
                    value={config.whatsapp?.messagebirdChannelId || ''}
                    onChange={(e) => updateConfig('whatsapp', 'messagebirdChannelId', e.target.value)}
                    placeholder="Your WhatsApp Channel ID"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={() => testConnection('whatsapp')}
                disabled={!config.whatsapp?.enabled}
              >
                Test WhatsApp Connection
              </Button>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <strong>Provider Guide:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li><strong>WhatsApp Web (QR):</strong> Free, uses your personal WhatsApp, ideal for small businesses</li>
              <li><strong>Twilio:</strong> Easy setup, sandbox available for testing</li>
              <li><strong>Meta Business API:</strong> Official API, requires Meta approval</li>
              <li><strong>WATI:</strong> No-code platform, easy templates</li>
              <li><strong>Interakt:</strong> Indian market focused, affordable</li>
              <li><strong>Gupshup:</strong> Enterprise grade, multi-channel</li>
              <li><strong>MessageBird:</strong> Global reach, omnichannel</li>
            </ul>
          </Alert>
        </Paper>
      )}

      {/* AI Configuration - Tab 2 */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            🤖 AI Configuration (Campaign Content Generation)
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Alert severity="info" sx={{ mb: 3 }}>
            Configure AI to generate campaign content, A/B test variations, and get performance predictions.
            Currently supports Google Gemini (free tier available).
          </Alert>

          <FormControlLabel
            control={
              <Switch
                checked={config.ai?.enabled ?? false}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setConfig((prev) => ({
                    ...prev,
                    ai: {
                      ...prev.ai,
                      enabled
                    },
                    aiApiKey: enabled ? (prev.aiApiKey || '') : ''
                  }));
                }}
              />
            }
            label="Enable AI Features"
            sx={{ mb: 3 }}
          />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>AI Provider</InputLabel>
                <Select
                  value={config.ai?.provider || 'GEMINI'}
                  onChange={(e) => updateConfig('ai', 'provider', e.target.value)}
                  label="AI Provider"
                  disabled={!config.ai?.enabled}
                >
                  <MenuItem value="GEMINI">Google Gemini (Recommended)</MenuItem>
                  <MenuItem value="OPENAI">OpenAI GPT</MenuItem>
                  <MenuItem value="CLAUDE">Anthropic Claude</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type={showPasswords.aiApiKey ? 'text' : 'password'}
                label="AI API Key"
                value={config.aiApiKey || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, aiApiKey: e.target.value }))}
                placeholder="Enter your API key"
                helperText={config.ai?.provider === 'GEMINI' ? 'Get free key at: makersuite.google.com/app/apikey' : ''}
                disabled={!config.ai?.enabled}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => togglePasswordVisibility('aiApiKey')} disabled={!config.ai?.enabled}>
                        {showPasswords.aiApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              AI Features Available:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Alert severity="success" sx={{ py: 0.5 }}>✨ Campaign Content Generation</Alert>
              <Alert severity="success" sx={{ py: 0.5 }}>🔀 A/B Test Variations</Alert>
              <Alert severity="success" sx={{ py: 0.5 }}>📊 Performance Predictions</Alert>
              <Alert severity="success" sx={{ py: 0.5 }}>⏰ Optimal Send Time</Alert>
              <Alert severity="success" sx={{ py: 0.5 }}>💡 Campaign Ideas</Alert>
              <Alert severity="success" sx={{ py: 0.5 }}>📈 Results Analysis</Alert>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Email Configuration - Tab 3 */}
      {activeTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Email Configuration
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={config.email.enabled}
                onChange={(e) => updateConfig('email', 'enabled', e.target.checked)}
              />
            }
            label="Enable Email Notifications"
            sx={{ mb: 3 }}
          />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Provider"
                value={config.email.provider}
                onChange={(e) => updateConfig('email', 'provider', e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="SMTP">SMTP</option>
                <option value="GMAIL">Gmail</option>
                <option value="SENDGRID">SendGrid</option>
                <option value="AWS_SES">AWS SES</option>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SMTP Host"
                value={config.email.host}
                onChange={(e) => updateConfig('email', 'host', e.target.value)}
                placeholder="smtp.gmail.com"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Port"
                value={config.email.port}
                onChange={(e) => updateConfig('email', 'port', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.email.secure}
                    onChange={(e) => updateConfig('email', 'secure', e.target.checked)}
                  />
                }
                label="Use SSL/TLS"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username/Email"
                value={config.email.user}
                onChange={(e) => updateConfig('email', 'user', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type={showPasswords.emailPassword ? 'text' : 'password'}
                label="Password"
                value={config.email.password}
                onChange={(e) => updateConfig('email', 'password', e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => togglePasswordVisibility('emailPassword')}>
                        {showPasswords.emailPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Email"
                value={config.email.from}
                onChange={(e) => updateConfig('email', 'from', e.target.value)}
                placeholder="noreply@company.com"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="From Name"
                value={config.email.fromName}
                onChange={(e) => updateConfig('email', 'fromName', e.target.value)}
                placeholder="Company Name"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={() => testConnection('email')}
                disabled={!config.email.enabled}
              >
                Test Email Connection
              </Button>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <strong>Gmail Users:</strong> Use an App Password instead of your regular password. 
            Generate one at: Google Account → Security → 2-Step Verification → App passwords
          </Alert>
        </Paper>
      )}

      {/* SMS Configuration - Tab 4 */}
      {activeTab === 4 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            SMS Configuration
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={config.sms.enabled}
                onChange={(e) => updateConfig('sms', 'enabled', e.target.checked)}
              />
            }
            label="Enable SMS Notifications"
            sx={{ mb: 3 }}
          />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>SMS Provider</InputLabel>
                <Select
                  value={config.sms.provider || 'TWILIO'}
                  onChange={(e) => updateConfig('sms', 'provider', e.target.value)}
                  label="SMS Provider"
                >
                  <MenuItem value="TWILIO">Twilio</MenuItem>
                  <MenuItem value="FIREBASE">Firebase Cloud Messaging</MenuItem>
                  <MenuItem value="TEXTLOCAL">TextLocal</MenuItem>
                  <MenuItem value="MSG91">MSG91</MenuItem>
                  <MenuItem value="NEXMO">Nexmo (Vonage)</MenuItem>
                  <MenuItem value="PLIVO">Plivo</MenuItem>
                  <MenuItem value="AWS_SNS">AWS SNS</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Twilio Fields */}
            {config.sms.provider === 'TWILIO' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Twilio Account SID"
                    value={config.sms.accountSid || ''}
                    onChange={(e) => updateConfig('sms', 'accountSid', e.target.value)}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type={showPasswords.twilioAuthToken ? 'text' : 'password'}
                    label="Twilio Auth Token"
                    value={config.sms.authToken || ''}
                    onChange={(e) => updateConfig('sms', 'authToken', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => togglePasswordVisibility('twilioAuthToken')}>
                            {showPasswords.twilioAuthToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                  )
                }}
              />
            </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="From Phone Number"
                    value={config.sms.fromNumber || ''}
                    onChange={(e) => updateConfig('sms', 'fromNumber', e.target.value)}
                    placeholder="+1234567890"
                  />
                </Grid>
              </>
            )}

            {/* TextLocal Fields */}
            {config.sms.provider === 'TEXTLOCAL' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="TextLocal API Key"
                    value={config.sms.apiKey || ''}
                    onChange={(e) => updateConfig('sms', 'apiKey', e.target.value)}
                    placeholder="Your TextLocal API Key"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Sender ID"
                    value={config.sms.senderId || ''}
                    onChange={(e) => updateConfig('sms', 'senderId', e.target.value)}
                    placeholder="TXTLCL"
                  />
                </Grid>
              </>
            )}

            {/* Firebase Fields */}
            {config.sms.provider === 'FIREBASE' && (
              <>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Upload your Firebase service account JSON file in the Push Notifications tab.
                  </Alert>
                </Grid>
              </>
            )}

            {/* MSG91 Fields */}
            {config.sms.provider === 'MSG91' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="MSG91 Auth Key"
                    value={config.sms.apiKey || ''}
                    onChange={(e) => updateConfig('sms', 'apiKey', e.target.value)}
                    placeholder="Your MSG91 Auth Key"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Sender ID"
                    value={config.sms.senderId || ''}
                    onChange={(e) => updateConfig('sms', 'senderId', e.target.value)}
                    placeholder="MSG91"
                  />
                </Grid>
              </>
            )}

            {/* Nexmo Fields */}
            {config.sms.provider === 'NEXMO' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nexmo API Key"
                    value={config.sms.apiKey || ''}
                    onChange={(e) => updateConfig('sms', 'apiKey', e.target.value)}
                    placeholder="Your Nexmo API Key"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type={showPasswords.nexmoSecret ? 'text' : 'password'}
                    label="Nexmo API Secret"
                    value={config.sms.authToken || ''}
                    onChange={(e) => updateConfig('sms', 'authToken', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => togglePasswordVisibility('nexmoSecret')}>
                            {showPasswords.nexmoSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="From Number"
                    value={config.sms.fromNumber || ''}
                    onChange={(e) => updateConfig('sms', 'fromNumber', e.target.value)}
                    placeholder="Nexmo"
                  />
                </Grid>
              </>
            )}

            {/* Plivo Fields */}
            {config.sms.provider === 'PLIVO' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Plivo Auth ID"
                    value={config.sms.accountSid || ''}
                    onChange={(e) => updateConfig('sms', 'accountSid', e.target.value)}
                    placeholder="Your Plivo Auth ID"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type={showPasswords.plivoToken ? 'text' : 'password'}
                    label="Plivo Auth Token"
                    value={config.sms.authToken || ''}
                    onChange={(e) => updateConfig('sms', 'authToken', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => togglePasswordVisibility('plivoToken')}>
                            {showPasswords.plivoToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="From Phone Number"
                    value={config.sms.fromNumber || ''}
                    onChange={(e) => updateConfig('sms', 'fromNumber', e.target.value)}
                    placeholder="+1234567890"
                  />
                </Grid>
              </>
            )}

            {/* AWS SNS Fields */}
            {config.sms.provider === 'AWS_SNS' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="AWS Access Key ID"
                    value={config.sms.accessKeyId || ''}
                    onChange={(e) => updateConfig('sms', 'accessKeyId', e.target.value)}
                    placeholder="AKIAIOSFODNN7EXAMPLE"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type={showPasswords.awsSecret ? 'text' : 'password'}
                    label="AWS Secret Access Key"
                    value={config.sms.secretAccessKey || ''}
                    onChange={(e) => updateConfig('sms', 'secretAccessKey', e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => togglePasswordVisibility('awsSecret')}>
                            {showPasswords.awsSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="AWS Region"
                    value={config.sms.region || ''}
                    onChange={(e) => updateConfig('sms', 'region', e.target.value)}
                    placeholder="us-east-1"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={() => testConnection('sms')}
                disabled={!config.sms.enabled}
              >
                Test SMS Connection
              </Button>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            {config.sms.provider === 'TWILIO' && <><strong>Get Twilio Credentials:</strong> Sign up at twilio.com → Console → Account SID & Auth Token</>}
            {config.sms.provider === 'TEXTLOCAL' && <><strong>Get TextLocal API Key:</strong> Sign up at textlocal.in → Settings → API Keys</>}
            {config.sms.provider === 'MSG91' && <><strong>Get MSG91 Auth Key:</strong> Sign up at msg91.com → API → Auth Key</>}
            {config.sms.provider === 'NEXMO' && <><strong>Get Nexmo Credentials:</strong> Sign up at nexmo.com → API Settings</>}
            {config.sms.provider === 'PLIVO' && <><strong>Get Plivo Credentials:</strong> Sign up at plivo.com → Account → Auth ID & Token</>}
            {config.sms.provider === 'AWS_SNS' && <><strong>Get AWS Credentials:</strong> AWS Console → IAM → Users → Security Credentials</>}
            {config.sms.provider === 'FIREBASE' && <><strong>Firebase SMS:</strong> Configure Firebase service account in Push Notifications tab</>}
          </Alert>
        </Paper>
      )}

      {/* Push Configuration - Tab 5 */}
      {activeTab === 5 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Push Notification Configuration
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <FormControlLabel
            control={
              <Switch
                checked={config.push.enabled}
                onChange={(e) => updateConfig('push', 'enabled', e.target.checked)}
              />
            }
            label="Enable Push Notifications"
            sx={{ mb: 3 }}
          />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="VAPID Subject"
                value={config.push.subject}
                onChange={(e) => updateConfig('push', 'subject', e.target.value)}
                placeholder="mailto:admin@company.com"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="VAPID Public Key"
                value={config.push.publicKey}
                onChange={(e) => updateConfig('push', 'publicKey', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type={showPasswords.vapidPrivateKey ? 'text' : 'password'}
                label="VAPID Private Key"
                value={config.push.privateKey}
                onChange={(e) => updateConfig('push', 'privateKey', e.target.value)}
                multiline
                rows={3}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => togglePasswordVisibility('vapidPrivateKey')}>
                        {showPasswords.vapidPrivateKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={generateVapidKeys}
              >
                Generate New VAPID Keys
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Firebase Service Account (for Firebase Push/SMS)
              </Typography>
              <Button variant="contained" component="label">
                {firebaseFileName || 'Upload Firebase JSON'}
                <input
                  type="file"
                  hidden
                  accept="application/json,.json"
                  onChange={handleFirebaseServiceAccountUpload}
                />
              </Button>
            </Grid>
          </Grid>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <strong>Warning:</strong> Generating new VAPID keys will invalidate all existing push subscriptions. 
            Users will need to re-subscribe to push notifications.
          </Alert>
        </Paper>
      )}

      {/* General Settings - Tab 6 */}
      {activeTab === 6 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            General Settings
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Rate Limits */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Rate Limits
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Email Per Hour"
                value={config.rateLimits.email.perHour}
                onChange={(e) => updateNestedConfig('rateLimits', 'email', 'perHour', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Email Per Day"
                value={config.rateLimits.email.perDay}
                onChange={(e) => updateNestedConfig('rateLimits', 'email', 'perDay', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="SMS Per Hour"
                value={config.rateLimits.sms.perHour}
                onChange={(e) => updateNestedConfig('rateLimits', 'sms', 'perHour', parseInt(e.target.value))}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="SMS Per Day"
                value={config.rateLimits.sms.perDay}
                onChange={(e) => updateNestedConfig('rateLimits', 'sms', 'perDay', parseInt(e.target.value))}
              />
            </Grid>

            {/* Email Templates */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Email Templates
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Primary Color"
                type="color"
                value={config.templates.email.primaryColor}
                onChange={(e) => updateNestedConfig('templates', 'email', 'primaryColor', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Logo URL"
                value={config.templates.email.logo}
                onChange={(e) => updateNestedConfig('templates', 'email', 'logo', e.target.value)}
                placeholder="https://company.com/logo.png"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Email Header"
                value={config.templates.email.header}
                onChange={(e) => updateNestedConfig('templates', 'email', 'header', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Email Footer"
                value={config.templates.email.footer}
                onChange={(e) => updateNestedConfig('templates', 'email', 'footer', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* WhatsApp QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={handleQrDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WhatsAppIcon sx={{ color: '#25D366' }} />
          Scan QR Code in WhatsApp
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            {qrCode ? (
              <>
                <QRCodeCanvas value={qrCode} size={250} />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Open WhatsApp on your phone → Settings → Linked Devices → Link a Device
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <strong>After linking, click "I've Scanned" to save!</strong>
                </Alert>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress />
                <Typography>Generating QR Code...</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setQrDialogOpen(false); setQrCode(null); }} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleQrDialogClose} variant="contained" color="success" disabled={!qrCode}>
            I've Scanned - Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationSettings;
